import { Auth0ProviderOptions } from '@auth0/auth0-react'
import { z } from 'zod'

import { CargoFormatterService, TransportUnitResponse } from '@forto-dev/renderer-templates'
import { RendererConfig } from '@forto-dev/renderer-types'

import { error as logError, logger } from '../utils/logger'

const configSchema = z.object({
  APP_ENV: z
    .enum(['development', 'production', 'sandbox', 'ode', ''])
    .nullish()
    .transform((data) => data ?? undefined),
  APP_BASE_URL: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  API_ENDPOINT: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  PUBLIC_API_ENDPOINT: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  FEATURE_TOGGLES: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  STORAGE_ENV: z
    .enum(['development', 'production', 'sandbox', ''])
    .nullish()
    .transform((data) => data ?? undefined),
  DATADOG_RUM_APP_ID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  DATADOG_RUM_CLIENT_TOKEN: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  DATADOG_RUM_SITE: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  DATADOG_RUM_SERVICE: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  FULLSTORY_ANALYTICS_ID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  USERLANE_ID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  SHIP_URL: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  GOOGLE_MAP_API_KEY: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  NOTIFICATION_UNSUBSCRIBE_URL: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  NOTIFICATION_KNOWLEDGE_BASE_URL: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  AUTH0_DOMAIN: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
  AUTH0_CLIENT_ID: z
    .string()
    .nullish()
    .transform((data) => data ?? undefined),
})

export type IConfig = z.infer<typeof configSchema>

// Error logging checks feature toggles, which read the config. Guarding
// against re-entrant logging breaks the otherwise infinite recursion
// (getConfig -> logError -> isEnabled -> getConfig -> ...) when the config
// cannot be loaded.
let loggingConfigError = false
const logConfigError = (message: string, ...args: unknown[]): void => {
  if (loggingConfigError) {
    return
  }
  loggingConfigError = true
  try {
    logError(message, ...args)
  } finally {
    loggingConfigError = false
  }
}

const loadConfig = (): IConfig => {
  if (typeof window.config === 'undefined') {
    throw new Error('Configuration is not initialized on window.config')
  }

  try {
    return configSchema.parse(window.config)
  } catch (error) {
    if (error instanceof z.ZodError) {
      logConfigError('Invalid configuration', JSON.stringify(error.errors))
    }
    throw new Error('Failed to load valid configuration')
  }
}

export const getConfig = (): IConfig => {
  let config: IConfig | null = null
  if (config === null) {
    try {
      config = loadConfig()
    } catch (error) {
      logConfigError('Failed to load configuration', error)
      throw error
    }
  }
  return config
}

export const getAuth0Config = (): Auth0ProviderOptions => {
  return {
    domain: window.config.AUTH0_DOMAIN || '',
    clientId: window.config.AUTH0_CLIENT_ID || '',
    authorizationParams: {
      redirect_uri: `${window.location.origin}/loginv2/callback`,
      audience: 'https://forto-global-api',
      scope: 'openid profile email',
    },
    cacheLocation: 'localstorage',
  }
}

export const getRendererConfig = (transportUnits: TransportUnitResponse[]): RendererConfig => {
  let config: RendererConfig | null = null
  if (config === null) {
    try {
      const appConfig = getConfig()
      config = {
        senderSecretKey: '',
        senderSecretSalt: '',
        serviceName: 'ship',
        shipBaseURL: appConfig.SHIP_URL!,
        tmsBaseURL: '', // Since we won't need to link to TMS from Ship
        unsubscribeBaseURL: appConfig.NOTIFICATION_UNSUBSCRIBE_URL!,
        knowledgeBaseURL: appConfig.NOTIFICATION_KNOWLEDGE_BASE_URL!,
        transportUnits: CargoFormatterService.transformResponses(transportUnits),
        additionalUserDetails: [],
        logger,
        renderMode: 'browser',
      } as RendererConfig
    } catch (error) {
      logConfigError('Failed to load renderer configuration', error)
      throw error
    }
  }
  return config
}
